import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import PlagResultCard from "./plagResultCard";
import animationData from "../../../constant/animations/plag.json";
import Lottie from "lottie-react";
import { useDispatch, useSelector } from "react-redux";
import { setPlagiarismOpen } from "../../../store/sop/actions";
import Progressbar from "./Progressbar";
import { getPlagiarism } from "../../../apiFunctions/sop/utils";
import { setPlagiarism } from "../../../store/sop/actions";
import {
  NotificationStatus,
  setNotification,
} from "../../../store/main/actions";
import { parseError } from "../../../utils/common";
import PremiumModal from "../../generic/PremiumModal";

export default function Example() {
  const dispatch = useDispatch();
  const selector = useSelector((state: any) => state.sop.plagiarismOpen);
  const isLoading = useSelector((state: any) => state.sop.loading);
  const editortext = useSelector((state: any) => state.sop.editorState);

  const sop_id = useSelector((state: any) => state.sop.sop_id);

  const [data, setData] = useState<any>(null);
  const [color, setColor] = useState<string>("bg-semantic-success");
  const [text, setText] = useState<string>("");
  const [premiumModalOpen, setPremiumModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isLoading) {
      getPlagiarism(editortext, sop_id)
        .then((res: any) => {
          dispatch(setPlagiarism(false));
          setData(res);

          let percentage = parseInt(res.percentPlagiarism);

          if (percentage >= 100) {
            percentage = 100;
          }

          if (
            percentage === undefined ||
            percentage === null ||
            isNaN(percentage)
          ) {
            percentage = 0;
          }

          if (percentage >= 0 && percentage <= 35) {
            setText("Excellent");
            setColor("#36B37E");
          } else if (percentage >= 36 && percentage <= 60) {
            setText("Average");
            setColor("#FFAB00");
          } else if (percentage >= 61 && percentage <= 100) {
            setText("Poor");
            setColor("#EF233C");
          } else {
            dispatch(setPlagiarismOpen(false));
            dispatch(
              setNotification({
                status: NotificationStatus.failed,
                message: "Unable to fetch plagiarism report!!",
              })
            );
          }
        })
        .catch((err) => {
          console.log(err);
          if (err.response?.status === 402) {
            dispatch(setPlagiarism(false));
            dispatch(setPlagiarismOpen(false));
            setPremiumModalOpen(true);
            return;
          }
          dispatch(setPlagiarism(false));
          dispatch(setPlagiarismOpen(false));
          dispatch(
            setNotification({
              status: NotificationStatus.failed,
              message:
                parseError(err) ||
                "Something went wrong, please contact support",
            })
          );
        });
    }
  }, [selector]);
  const style = {
    background: "rgba(0, 0, 0, 0)",
  };
  const CloseModal = () => {
    if (isLoading) {
      return;
    }
    dispatch(setPlagiarismOpen(false));
  };

  return (
    <>
      <PremiumModal open={premiumModalOpen} setOpen={setPremiumModalOpen} />
      <div>
        <Transition.Root show={selector || false} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50 hidden md:block"
            onClose={CloseModal}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative flex w-full sm:max-w-sm max-w-[85vw] flex-1 flex-col bg-[#101623]">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="flex flex-row justify-between py-2 px-4 items-center mt-2">
                      <p className="text-2xl font-bold text-white">
                        Plagiarism Report
                      </p>
                      {isLoading ? null : (
                        <button
                          type="button"
                          className="ml-1 flex h-10 w-10 items-center justify-center"
                          onClick={() => {
                            dispatch(setPlagiarismOpen(false));
                          }}
                        >
                          <span className="sr-only">Close sidebar</span>
                          <XMarkIcon
                            className="h-6 w-6 text-white"
                            aria-hidden="true"
                          />
                        </button>
                      )}
                    </div>
                  </Transition.Child>
                  <hr className="my-4 border-primary-400" />

                  {isLoading ? (
                    <div className="flex flex-col flex-center justify-center">
                      <Lottie
                        animationData={animationData}
                        loop={true}
                        autoplay={true}
                        style={style}
                        height={150}
                        width={150}
                      />
                      <p className="text-2xl text-white text-center">
                        Scanning for similarity....
                      </p>
                    </div>
                  ) : (
                    <div className="h-0 flex-1 overflow-y-auto pt-5 p-2">
                      <nav className="space-y-1 px-2">
                        <div className="flex flex-row justify-center items-center gap-4 mb-5">
                          <Progressbar
                            size={150}
                            value={data?.percentPlagiarism}
                            max={100}
                            strokeWidth={14}
                            stroke={color}
                          />
                          <div className="flex flex-col">
                            <p className="text-white text-2xl font-semibold mb-2">
                              Overall Score:
                            </p>
                            <p className="text-white text-lg text-break">
                              You have an{" "}
                              <span
                                style={{ color: color, fontWeight: "bold" }}
                              >
                                {text}{" "}
                              </span>
                              Score
                            </p>
                          </div>
                        </div>
                        <p className="text-xl text-white">Sources</p>
                        <div className="flex flex-col space-y-3 items-center">
                          {data?.sources?.map((source: any, index: number) => {
                            return (
                              <PlagResultCard
                                key={source}
                                color={color}
                                source={source.url}
                                title={source.title}
                              />
                            );
                          })}
                        </div>
                      </nav>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for mobile */}
      </div>

      <div>
        <Transition.Root show={selector || false} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50 md:hidden"
            onClose={CloseModal}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center md:p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative border-t-2 md:border-2 border-blue-500 w-screen md:w-[95vw] lg:w-[90vw] h-[95vh] md:max-w-screen-2xl transform overflow-hidden rounded-t-xl md:rounded-2xl bg-[#101623] text-left shadow-xl transition-all">
                    <div className="flex flex-row justify-between px-4 py-2 items-center">
                      <p className="text-2xl font-bold text-white">
                        Plagiarism Report
                      </p>
                      {isLoading ? null : (
                        <button
                          type="button"
                          className="ml-1 flex h-10 w-10 items-center justify-center"
                          onClick={() => {
                            dispatch(setPlagiarismOpen(false));
                          }}
                        >
                          <span className="sr-only">Close sidebar</span>
                          <XMarkIcon
                            className="h-6 w-6 text-white"
                            aria-hidden="true"
                          />
                        </button>
                      )}
                    </div>
                    <hr className="my-2 border-primary-400" />

                    {isLoading ? (
                      <div className="flex flex-col flex-center justify-center">
                        <Lottie
                          animationData={animationData}
                          loop={true}
                          autoplay={true}
                          style={style}
                          height={150}
                          width={150}
                        />
                        <p className="text-2xl text-white text-center">
                          Scanning for similarity....
                        </p>
                      </div>
                    ) : (
                      <div className="flex-1 flex-col h-full overflow-y-auto mt-3 pb-20">
                        <div className="flex flex-row justify-center items-center gap-4 mb-5">
                          <div className="flex flex-col w-1/2 items-center">
                            <Progressbar
                              size={150}
                              value={data?.percentPlagiarism}
                              max={100}
                              strokeWidth={14}
                              stroke={color}
                            />
                          </div>
                          <div className="flex flex-col w-1/2">
                            <p className="text-white text-2xl font-semibold mb-2">
                              Overall Score:
                            </p>
                            <p className="text-white text-lg text-break">
                              You have an{" "}
                              <span
                                style={{ color: color, fontWeight: "bold" }}
                              >
                                {text}{" "}
                              </span>
                              Score
                            </p>
                          </div>
                        </div>
                        <p className="text-lg text-white mx-4">Sources: </p>
                        <div className="flex flex-col items-center m-1 p-2">
                          {data?.sources?.map((source: any, index: number) => {
                            return (
                              <PlagResultCard
                                key={source}
                                color={color}
                                source={source.url}
                                title={source.title}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      </div>
    </>
  );
}
